import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ShieldCheck, CheckCircle2, XCircle, Clock, ArrowRight, FileText, Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DrawScoreDemo from '@/components/DrawScoreDemo';

/**
 * HeroVariants — three distinct home/hero concepts for the "aspirin" positioning.
 * Clickable preview so the owner can pick a direction. Route: /hero-variants.
 * All copy uses "Built to get approved" (no liability claim).
 */
export default function HeroVariants() {
  const navigate = useNavigate();
  const go = () => navigate('/signup');

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 flex-wrap">
        <span className="font-bold text-slate-800">Hero concepts</span>
        <span className="text-xs text-slate-400">Pick one — clickable preview, fake data</span>
        <div className="ml-auto flex gap-2 text-xs">
          <a href="#opt1" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">1 · The Score</a>
          <a href="#opt2" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">2 · Rejected → Approved</a>
          <a href="#opt3" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">3 · 30-Second Clock</a>
        </div>
      </div>

      <Label n="1" name="The Score — product-led, the live gauge is the hero" />
      <VariantScore go={go} />

      <Label n="2" name="Rejected → Approved — pain-to-relief, bold & emotional" />
      <VariantFlip go={go} />

      <Label n="3" name="The 30-Second Clock — speed-led, energetic" />
      <VariantClock go={go} />

      <div className="py-16 text-center text-slate-400 text-sm">End of concepts · tell me which number to ship</div>
    </div>
  );
}

function Label({ n, name }) {
  return (
    <div id={`opt${n}`} className="max-w-6xl mx-auto px-6 pt-14 pb-4">
      <div className="inline-flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center">{n}</span>
        <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{name}</span>
      </div>
    </div>
  );
}

/* ─────────────── Option 1 — The Score ─────────────── */
function VariantScore({ go }) {
  return (
    <section className="mx-auto max-w-6xl rounded-3xl overflow-hidden shadow-xl bg-slate-950 text-white">
      <div className="grid lg:grid-cols-2 gap-8 items-center p-10 lg:p-14 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-blue-300 mb-5">
            <Gauge className="w-4 h-4" /> Live approval score
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-5">
            Your draw, <span className="text-blue-400">scored live.</span><br />Approved in 30 seconds.
          </h1>
          <p className="text-lg text-slate-300 mb-8 max-w-md">
            Watch a single number tell you if your draw will clear. Follow it to 100 and submit a bank-ready package. Built to get approved.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={go} className="bg-blue-600 hover:bg-blue-700 text-lg py-6 px-8">Submit Your First Draw <ArrowRight className="w-5 h-5 ml-2" /></Button>
            <Button variant="outline" onClick={go} className="border-slate-600 text-white hover:bg-white/10 text-lg py-6 px-8">See it work</Button>
          </div>
        </div>
        <div className="relative"><DrawScoreDemo /></div>
      </div>
    </section>
  );
}

/* ─────────────── Option 2 — Rejected → Approved flip ─────────────── */
function VariantFlip({ go }) {
  const [approved, setApproved] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setApproved((a) => !a), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="mx-auto max-w-6xl rounded-3xl overflow-hidden shadow-xl bg-white border border-slate-200">
      <div className="grid lg:grid-cols-2 gap-8 items-center p-10 lg:p-14">
        <div>
          <div className="inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-red-50 text-red-600 mb-5">Stop the rejections</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] mb-5">
            Never hear<br /><span className="text-red-500 line-through decoration-4">"draw rejected"</span><br />again.
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-md">
            One missing doc and your draw bounces — costing you days of interest. DrawReady tells you exactly what your bank needs before you hit submit. Built to get approved.
          </p>
          <Button onClick={go} className="bg-slate-900 hover:bg-slate-800 text-lg py-6 px-8">Clear My Draw Headache <ArrowRight className="w-5 h-5 ml-2" /></Button>
        </div>
        <div className="flex items-center justify-center">
          <div className={`relative w-72 h-72 rounded-3xl flex flex-col items-center justify-center transition-all duration-700 ${approved ? 'bg-emerald-50 ring-4 ring-emerald-300 rotate-0' : 'bg-red-50 ring-4 ring-red-300 -rotate-3'}`}>
            {approved ? (
              <>
                <CheckCircle2 className="w-20 h-20 text-emerald-500" />
                <p className="mt-4 text-2xl font-extrabold text-emerald-600 uppercase tracking-wide">Approved</p>
                <p className="text-xs text-emerald-500 mt-1">DrawReady score 100</p>
              </>
            ) : (
              <>
                <XCircle className="w-20 h-20 text-red-400" />
                <p className="mt-4 text-2xl font-extrabold text-red-500 uppercase tracking-wide">Rejected</p>
                <p className="text-xs text-red-400 mt-1">missing COI · wrong total</p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Option 3 — 30-Second Clock ─────────────── */
function VariantClock({ go }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    let raf;
    const start = Date.now();
    const loop = () => {
      const p = ((Date.now() - start) % 4000) / 4000;
      setPct(p);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  const steps = [
    { icon: Zap, t: 'Tap Quick Draw' },
    { icon: ShieldCheck, t: 'Score climbs' },
    { icon: CheckCircle2, t: 'Fix 2 things' },
    { icon: FileText, t: 'Submit' },
  ];
  const activeStep = Math.min(3, Math.floor(pct * 4));
  return (
    <section className="mx-auto max-w-6xl rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
      <div className="p-10 lg:p-14 text-center">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-cyan-50 mb-4">
          <Clock className="w-4 h-4" /> Fastest draw in construction
        </div>
        <h1 className="text-5xl lg:text-7xl font-extrabold leading-none mb-3">
          A bank-ready draw<br />in <span className="underline decoration-white/40 decoration-8 underline-offset-4">30 seconds.</span>
        </h1>
        <p className="text-lg text-cyan-50/90 mb-8 max-w-xl mx-auto">Built to get approved — no guesswork, no back-and-forth.</p>

        {/* animated flow bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="h-2 rounded-full bg-white/25 overflow-hidden mb-4">
            <div className="h-full bg-white rounded-full" style={{ width: `${pct * 100}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {steps.map((s, i) => (
              <div key={i} className={`flex flex-col items-center gap-1.5 transition-opacity ${i <= activeStep ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${i <= activeStep ? 'bg-white text-blue-600' : 'bg-white/20 text-white'}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="text-[11px] font-medium">{s.t}</span>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={go} className="bg-white text-blue-700 hover:bg-cyan-50 text-lg py-6 px-8 font-bold">Start Your 30-Second Draw <ArrowRight className="w-5 h-5 ml-2" /></Button>
      </div>
    </section>
  );
}
